import { useContext, useEffect, useState, useRef } from 'react'
import { getBrazeSDK } from '../../Config/Braze/braze.config'
import { AuthContext } from '../../Api/firebase/authContext'
import { StorageContext } from '../../Context/StorageContext'

export type BrazeContentCard = {
  id: string
  created_at: number
  expires_at: number | null
  dismissed: boolean
  dismissed_at: number | null
  viewed: boolean
  clicked: boolean
  pinned: boolean
  title: string
  description: string
  aspect_ratio?: string
  imageUrl?: string
  linkText?: string
  url?: string
  extras?: Record<string, unknown>
}

export type UseBrazeContentCardsReturn = {
  contentCards: BrazeContentCard[]
  isSubscribed: boolean
  isLoadingCards: boolean
  refreshCards: () => Promise<void>
  dismissCard: (cardId: string) => Promise<void>
}

/**
 * Hook to handle Braze Content Cards
 * Subs to content cards from Braze
 * @param brazeInitialized Whether Braze is initialized
 */
export const useBrazeContentCards = (
  brazeInitialized?: boolean
): UseBrazeContentCardsReturn => {
  const { authLoading, uid } = useContext(AuthContext)!
  const { isStorageReady, store, getStoredData } = useContext(StorageContext)!
  const [contentCards, setContentCards] = useState<BrazeContentCard[]>([])
  const [isLoadingCards, setIsLoadingCards] = useState(true)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const subscriptionIdRef = useRef<string | null>(null)

  useEffect(() => {
    getStoredContentCards()

    setupContentCards()

    return () => {
      if (subscriptionIdRef.current) {
        cleanup()
      }
    }
  }, [authLoading, brazeInitialized, uid])

  const setupContentCards = async () => {
    if (authLoading || !brazeInitialized) return
    try {
      const braze = await getBrazeSDK()
      if (!braze) return

      subscriptionIdRef.current = braze.subscribeToContentCardsUpdates(
        (cardsRes: { cards: BrazeContentCard[] }) => {
          if (cardsRes?.cards?.length) {
            setContentCards(cardsRes.cards)
            isStorageReady && store('contentCards', cardsRes.cards)
          }
        }
      )
      braze.requestContentCardsRefresh()
      setIsSubscribed(true)
    } catch (error) {
      console.error('Error setting up Braze content cards:', error)
    }
  }

  const refreshCards = async () => {
    try {
      const braze = await getBrazeSDK()
      if (!braze) return
      const newCards = await braze.requestContentCardsRefresh()
      if (newCards?.cards?.length) {
        setContentCards(newCards.cards)
        isStorageReady && store('contentCards', newCards.cards)
      }
    } catch (error) {
      console.error('Error refreshing content cards:', error)
    }
  }

  const dismissCard = async (cardId: string) => {
    try {
      const braze = await getBrazeSDK()
      if (!braze) return

      const card = contentCards.find((c) => c.id === cardId)
      if (card) {
        card.dismissed = true
        setContentCards((prev) => prev.filter((c) => c.id !== cardId))
        isStorageReady && store('contentCards', contentCards)
      }
    } catch (error) {
      console.error('Error dismissing content card:', error)
    }
  }

  const getStoredContentCards = async () => {
    const storedContentCards = await getStoredData('contentCards')
    if (storedContentCards?.data) {
      setContentCards(storedContentCards.data)
    }
    setIsLoadingCards(false)
  }

  /**
 * Cleanup the subscription to Braze content cards
 */
  const cleanup = async () => {
    try {
      const braze = await getBrazeSDK()
      if (!braze) return
      console.log('Removing subscription:', subscriptionIdRef.current)
      braze.removeSubscription(subscriptionIdRef.current)
    } catch (error) {
      console.error('Error cleaning up Braze subscription:', error)
    }
  }

  return {
    contentCards,
    isSubscribed,
    isLoadingCards,
    refreshCards,
    dismissCard,
  }
}
